import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/header";
import API_BASE_URL from "../../utils/apiConfig";
import { FaArrowLeft, FaUpload, FaTrash, FaCopy, FaCheck } from "react-icons/fa";

interface MediaItem {
  id: number;
  filename: string;
  original_name: string;
  path: string;
  mime_type: string;
  size: number;
  alt_text: string | null;
  url: string;
}

export default function CmsMediaManager() {
  const navigate = useNavigate();
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (!userData || JSON.parse(userData).role !== "admin") {
      navigate("/home");
      return;
    }
    fetchMedia();
  }, [navigate]);

  const fetchMedia = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/cms/admin/media`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setMedia(await res.json());
    } catch (err) {
      console.error("Failed to load media:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API_BASE_URL}/api/cms/admin/media`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        await fetchMedia();
      }
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this image?")) return;
    const token = localStorage.getItem("token");
    await fetch(`${API_BASE_URL}/api/cms/admin/media/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    setMedia((prev) => prev.filter((m) => m.id !== id));
  };

  const copyUrl = (id: number, url: string) => {
    const fullUrl = `${API_BASE_URL}${url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / 1048576).toFixed(1) + " MB";
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-[#0a2e30] to-[#061e1f] pt-24 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate("/cms")} className="cursor-pointer bg-white/10 hover:bg-white/20 text-white rounded-lg p-3 transition-colors">
                <FaArrowLeft />
              </button>
              <h1 className="text-2xl md:text-3xl font-bold text-white">Media Manager</h1>
            </div>
            <label className="cursor-pointer flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all">
              <FaUpload /> {uploading ? "Uploading..." : "Upload Image"}
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" disabled={uploading} />
            </label>
          </div>

          {loading ? (
            <div className="text-white text-center py-12">Loading media...</div>
          ) : media.length === 0 ? (
            <div className="text-center py-20">
              <FaImages className="text-6xl text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No media uploaded yet</p>
              <p className="text-gray-500 text-sm">Upload images to use in your website content</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {media.map((item) => (
                <div key={item.id} className="bg-white/5 rounded-xl overflow-hidden border border-white/10 group hover:border-emerald-500/50 transition-colors">
                  <div className="aspect-square overflow-hidden bg-black/20">
                    <img src={`${API_BASE_URL}${item.url}`} alt={item.alt_text || item.original_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <div className="p-3">
                    <p className="text-white text-sm truncate font-medium">{item.original_name}</p>
                    <p className="text-gray-500 text-xs">{formatSize(item.size)}</p>
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => copyUrl(item.id, item.url)} className="cursor-pointer flex-1 flex items-center justify-center gap-1 bg-white/10 hover:bg-white/20 text-white text-xs py-2 rounded-lg transition-colors">
                        {copiedId === item.id ? <><FaCheck /> Copied!</> : <><FaCopy /> Copy URL</>}
                      </button>
                      <button onClick={() => handleDelete(item.id)} className="cursor-pointer bg-red-500/20 hover:bg-red-500/40 text-red-400 p-2 rounded-lg transition-colors">
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </>
  );
}

function FaImages(props: any) {
  return <svg {...props} viewBox="0 0 576 512" fill="currentColor" width="1em" height="1em"><path d="M512 32H160c-35.3 0-64 28.7-64 64v12H64c-35.3 0-64 28.7-64 64v224c0 35.3 28.7 64 64 64h352c35.3 0 64-28.7 64-64v-12h32c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64z"/></svg>;
}
